import { CommonModule } from '@angular/common';
import { HttpClient } from '@angular/common/http';
import { Component, Inject, Injectable, OnInit } from '@angular/core';
import { FormControl, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialog, MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { BehaviorSubject, Observable, Subject } from 'rxjs';
import { of } from 'rxjs';
import { catchError, map } from 'rxjs/operators';

import { environment } from '../../../environments/environment';
import { TranslateService } from '@ngx-translate/core';
import { TranslateHttpLoader } from '@ngx-translate/http-loader';
import { PushModule } from '@rx-angular/template/push';
import { StorageService } from '@services/common/storage.service';
import { LoaderModule } from '@ui-kit/loader/loader.module';

@Injectable({
  providedIn: 'root'
})
export class TranslationService {
  editMode = false;
  private _loadingTranslationKey: BehaviorSubject<boolean> = new BehaviorSubject(false);
  private onLanguageChanged = new Subject<string>();
  private onEditMode = new BehaviorSubject<boolean>(false);
  languageChanged$ = this.onLanguageChanged.asObservable();
  editModeChanged$ = this.onEditMode.asObservable();
  get $loadingTranslationKey(): Observable<boolean> {
    return this._loadingTranslationKey.asObservable();
  }

  constructor(
    private translate: TranslateService,
    private _localStorage: StorageService,
    private http: HttpClient,
    private dialog: MatDialog
  ) {
    this.addLanguages(['en', 'ru', 'uk', 'nb']);

    this.editMode = this._localStorage.getItem('translation_edit_mode');
  }

  changeEditMode() {
    this.editMode = !this.editMode;
    this.onEditMode.next(this.editMode);
  }

  addLanguages(lang: string[]) {
    this.translate.addLangs(lang);
  }

  setDefaultLanguage(lang: string) {
    this.translate.setDefaultLang(lang);
  }

  getDefaultLanguage() {
    return this.translate.defaultLang;
  }

  getBrowserLanguage() {
    return this.translate.getBrowserLang();
  }

  getCurrentLanguage() {
    return this.translate.currentLang;
  }

  getLoadedLanguages() {
    return this.translate.langs;
  }

  useBrowserLanguage(): string | void {
    const browserLang = this.getBrowserLanguage();

    if (browserLang.match(/en|ru|ko/)) {
      this.changeLanguage(browserLang);
      return browserLang;
    }
  }

  useDefaultLanguage() {
    return this.changeLanguage(null);
  }

  changeLanguage(language: string) {
    if (!language) {
      language = this.getDefaultLanguage();
    }

    // tslint:disable-next-line:triple-equals
    if (language != this.translate.currentLang) {
      setTimeout(() => {
        this.translate.use(language);
        this.onLanguageChanged.next(language);
      });
    }

    return language;
  }

  emitLanguageChanged(language: string) {
    this.onLanguageChanged.next(language);
  }

  getTranslation(key: string | Array<string>, interpolateParams?: Object): string | any {
    return this.translate.instant(key, interpolateParams);
  }

  getTranslationAsync(key: string | Array<string>, interpolateParams?: Object): Observable<string | any> {
    return this.translate.get(key, interpolateParams);
  }

  getStringId(string): Observable<any> {
    return of();
    // const headers = new HttpHeaders().set('Authorization', 'Bearer 857372483f222c45a39634c6af303213d4d2097eb7cf1a152805aaf0b880f668fbf2dba9b5a7f69f'); // create header object
    // return this.http.get(environment.crowdinApiUrl + '/projects/' + environment.crowdinProjectId + '/strings?filter=' + string, {
    //   headers: headers
    // }).pipe(
    //   map((response: any) => {
    //     const res = response.data;
    //     let stringId = null;
    //     if (res.length === 1) {
    //       stringId = res[0].data.id;
    //       return stringId;
    //     }
    //
    //     return stringId;
    //
    //   })
    // );
  }

  updateStringTranslation({ stringId, text }): Observable<any> {
    return of(text);
    // const currentLang = this.getCurrentLanguage();
    //
    // const headers = new HttpHeaders().set('Authorization', 'Bearer 857372483f222c45a39634c6af303213d4d2097eb7cf1a152805aaf0b880f668fbf2dba9b5a7f69f');
    //
    // const data = {
    //   stringId: stringId,
    //   languageId: currentLang,
    //   text: text
    // };
    //
    // return this.http.post(environment.crowdinApiUrl + '/projects/' + environment.crowdinProjectId + '/translations', data, {
    //   headers: headers
    // }).pipe(
    //   catchError( (error): any => {
    //     return throwError(error);
    //   })
    // ).pipe(
    //   map(response => {
    //       return  response;
    //     }
    //   )
    // );
  }

  openTranslatableModal(key) {
    const value = this.translate.instant(key);

    this._loadingTranslationKey.next(true);

    const data = {
      title: 'Update Translation',
      key,
      value,
      stringId: null
    };

    const dialogRef = this.dialog.open(UpdateTranslationItemComponent, {
      width: '480px',
      data
    });

    this.getStringId(key).subscribe((id) => {
      this._loadingTranslationKey.next(false);
      if (id) {
        data.stringId = id;
      }
    });

    return dialogRef.afterClosed();
  }
}

@Component({
  standalone: true,
  imports: [CommonModule, PushModule, MatDialogModule, LoaderModule, ReactiveFormsModule],
  template: `
    <h1 mat-dialog-title class="font-bold">{{ data.title }}</h1>

    <mat-dialog-content class="mat-typography">
      <div class="mb-2 text-green-field" *ngIf="present == true">String present in translations list</div>
      <div class="mb-2 text-s4-bloody-marry" *ngIf="present == false">String is not present in translations list</div>

      <div class="mb-2">Source String: "{{ data.key }}"</div>

      <ng-container>
        <textarea class="form-controls__input" [formControl]="control"></textarea>
        <p class="error" *ngIf="control.hasError('required') && control?.touched">This field is required!</p>
        <p class="error" *ngIf="control.hasError('minlength') && control?.touched">This field is to small!</p>
      </ng-container>
    </mat-dialog-content>

    <mat-dialog-actions align="end">
      <button [mat-dialog-close]="true" (click)="update()">Save</button>
      <button class="ml-2" [mat-dialog-close]="false">Cancel</button>
    </mat-dialog-actions>
  `
})
export class UpdateTranslationItemComponent implements OnInit {
  control: FormControl;
  form: FormGroup;
  value: string;
  stringId: string;
  loadingTranslationKey: Observable<boolean>;

  present = false;

  constructor(
    private http: HttpClient,
    private translate: TranslateService,
    private translationService: TranslationService,
    public dialogRef: MatDialogRef<any>,
    @Inject(MAT_DIALOG_DATA) public data: any
  ) {
    this.loadingTranslationKey = translationService.$loadingTranslationKey;
  }

  ngOnInit(): void {
    this.present = this.hasTranslation(this.data.key, this.translationService.getCurrentLanguage());

    this.control = new FormControl(this.data.value, [Validators.required, Validators.minLength(1)]);
  }

  hasTranslation(translationKey: string, language?: string): boolean {
    const translations = this.translate.translations[language || this.translate.currentLang];
    return translations && translations[translationKey] !== undefined;
  }

  update(): void {
    const lang = this.translationService.getCurrentLanguage();
    const key = this.data.key;
    const value = this.control.value;

    this.translationService.updateStringTranslation({ stringId: this.data.stringId, text: value }).subscribe(
      (data) => {
        this.dialogRef.close();
        const translationObj = {};
        translationObj[key] = value;

        this.translate.setTranslation(lang, translationObj);
      },
      (error) => {}
    );
  }

  changeLanguage(lang): void {
    this.translationService.changeLanguage(lang);
  }
}

export class TranslateHttpLoader2 {
  private http;
  prefix: string;
  suffix: string;
  constructor(http: HttpClient, prefix?: string, suffix?: string) {
    this.http = http;
    this.prefix = prefix;
  }

  /**
   * Gets the translations from the server
   */
  getTranslation(lang: string): void {
    return this.http.get(this.prefix + lang).pipe(
      map(({ data }) => data),
      catchError(() => of({}))
    );
  }
}

export function createTranslateLoader(http: HttpClient): TranslateHttpLoader | TranslateHttpLoader2 {
  const localHosts = ['localhost'];

  if (localHosts.includes(window.location.hostname)) {
    return new TranslateHttpLoader(http, './assets/swen-json/', '/swen.json');
  }

  const path = environment.apiUrl + '/v1/translations/';
  return new TranslateHttpLoader2(http, path, '');
}
